import React, { useState, useEffect } from 'react';
import "./style.css";
import { getInformationUser as fetchUserInformation, mapControls as mapControlsService, getControlMapperFromBackend, saveControlMapperToBackend } from '../../services/objectiveAIServices';
import { toast } from 'react-toastify';
let BASE_URL = process.env.REACT_APP_API_URL;

async function loadConfig() {
    try {
        const response = await fetch("/config.json");
        const config = await response.json();
        BASE_URL = config.API_URL || BASE_URL; 
    } catch (error) {
        // Failed to load config.json, using default .env API URL
    }
}

loadConfig();

async function getCsrfToken(): Promise<string> {
  const response = await fetch(`${BASE_URL}/csrf/token`, { method: "GET", credentials: "include" });
  const data = await response.json();
  return data.csrfToken;
}

function getToken() {
    return localStorage.getItem("d_c_c_t");
}

interface MappedControl {
  control: {
    id: string;
    name: string;
    description: string;
    code: string;
    objective?: string;
    mandatoryType?: string;
  };
  suggested_owner_role_department?: {
    id: string;
    name: string;
    description: string;
    code: string;
  };
}

interface ComplianceFramework {
  id: string;
  name: string;
  reason: string;
}

interface UserData {
  applicable_compliance_frameworks: {
    value: ComplianceFramework[];
    source: string[];
  };
  organization_name: {
    value: string;
    source: string[];
  };
  headquarters_country: {
    value: string;
    source: string[];
  };
  industry_sectors: {
    value: string[];
    source: string[];
  };
  entity_type: {
    value: string;
    source: string[];
  };
  key_services: {
    value: string[];
    source: string[];
  };
  company_size: {
    value: {
      employees?: number;
      revenue_band?: string;
    };
    source: string[];
  };
  business_constraints: {
    value: {
      budget_usd?: number | null;
      available_resources?: string[];
      preferred_start_date?: string;
    };
    source: string[];
  };
  compliance_certifications: {
    value: string[];
    source: string[];
  };
  regulatory_registrations: {
    value: string[];
    source: string[];
  };
  regulatory_details: {
    supervisory_authority: {
      value: string;
      source: string[];
    };
    license_type: {
      value: string;
      source: string[];
    };
    license_number: {
      value: string;
      source: string[];
    };
    lei_code: {
      value: string;
      source: string[];
    };
    regulatory_status: {
      value: string;
      source: string[];
    };
  };
  recent_press: {
    value: Array<{
      title: string;
      date: string;
      url: string;
    }>;
    source: string[];
  };
  institution_id: {
    value: string;
    source: string[];
  };
}

interface UserInformation {
  createdAt: string;
  data: UserData[];
  deletedAt: null | string;
  id: string;
  isPublic: boolean;
  isSuperAdmin: boolean;
  tenant: {
    id: string;
    name: string;
    contactInfo: null | string;
    image: null | string;
    createdBy: string;
  };
  tenantId: string;
  updatedAt: string;
  user: {
    tenantId: string;
    isPublic: boolean;
    isSuperAdmin: boolean;
    id: string;
    email: string;
  };
  userId: string;
}

interface ControlsResponse {
  success: boolean;
  data: {
    grouped: Array<{
      name: string;
      description: string;
      objective: string;
      mapped_controls: Array<{
        id: string;
        name: string;
        description: string;
        code: string;
        objective?: string;
        mandatoryType?: string;
      }>;
      suggested_owner_role_department?: {
        id: string;
        name: string;
        description: string;
        code: string;
      };
    }>;
    individual: Array<{
      control: {
        id: string;
        name: string;
        description: string;
        code: string;
        mandatoryType?: string;
      };
      suggested_owner_role_department?: {
        id: string;
        name: string;
        description: string;
        code: string;
      };
    }>;
  };
}

const UserInformationDashboard: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInformation | null>(null);
  const [controlsData, setControlsData] = useState<ControlsResponse | null>(null);
  const [mappingComplete, setMappingComplete] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const infoResponse = await getInformationUser();
        setUserInfo(infoResponse);

        // 1. First, check backend for existing control mapper data
        const backendControlMapper = await getControlMapperFromBackend();
        
        if (backendControlMapper && (
          (backendControlMapper.individual && Array.isArray(backendControlMapper.individual) && backendControlMapper.individual.length > 0) ||
          (backendControlMapper.grouped && Array.isArray(backendControlMapper.grouped) && backendControlMapper.grouped.length > 0)
        )) {
          // Use control mapper data from backend
          setControlsData({
            success: true,
            data: backendControlMapper
          });
          setMappingComplete(true);
          toast.success("Control mapper data loaded from saved data");
          setLoading(false);
          return; // Exit early, no need to call AI API
        }

        // 2. No control mapper data in backend, fetch from AI API
        if (infoResponse?.data?.[0]?.applicable_compliance_frameworks) {
          const frameworksData = infoResponse.data[0].applicable_compliance_frameworks;
          
          let frameworkIds: string[] = [];
          
          // Handle new structure: { value: [{id: "...", name: "..."}, ...], source: [...] }
          if (frameworksData?.value && Array.isArray(frameworksData.value)) {
            frameworkIds = frameworksData.value
              .filter(f => {
                const isValid = f && typeof f === 'object' && f.id;
                return isValid;
              })
              .map(f => {
                const id = f.id;
                return id;
              });
          }
          
          if (frameworkIds.length > 0) {
            const result = await mapControls({ applicable_frameworks: frameworkIds });
            
            // Check if the AI API response is successful and has data
            if (result.success && result.data) {
              setControlsData(result);
              setMappingComplete(true);
              
              // Save the AI response to backend
              const saveResult = await saveControlMapperToBackend(result.data);
              if (saveResult.success) {
                toast.success("Control mapper data loaded and saved successfully");
              } else {
                toast.warning("Control mapper data loaded but failed to save: " + (saveResult.message || "Unknown error"));
              }
            } else {
              setMappingComplete(true);
            }
          } else {
            setMappingComplete(true);
          }
        } else {
          setMappingComplete(true);
        }
              } catch (err) {
          setError(err instanceof Error ? err.message : 'An unknown error occurred');
          setMappingComplete(true); // Ensure mapping is marked as complete even on error
              } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);
  
  // Effect to call API when Controls tab is activated
  useEffect(() => {
    if (activeTab === 'controls' && userInfo) {
      extractFrameworkIdsAndCallAPI();
    }
  }, [activeTab, userInfo]);
  
  // Function to extract framework IDs and call API
  const extractFrameworkIdsAndCallAPI = async () => {
    try {
      // First check backend for existing control mapper data
      const backendControlMapper = await getControlMapperFromBackend();
      
      if (backendControlMapper && (
        (backendControlMapper.individual && Array.isArray(backendControlMapper.individual) && backendControlMapper.individual.length > 0) ||
        (backendControlMapper.grouped && Array.isArray(backendControlMapper.grouped) && backendControlMapper.grouped.length > 0)
      )) {
        // Use control mapper data from backend
        setControlsData({
          success: true,
          data: backendControlMapper
        });
        setMappingComplete(true);
        return; // Exit early, no need to call AI API
      }
      
      if (!userInfo?.data?.[0]?.applicable_compliance_frameworks) {
        return;
      }
      
      const userData = userInfo.data[0];
      const frameworksData = userData.applicable_compliance_frameworks;
      
      let frameworkIds: string[] = [];
      
      // Handle new structure: { value: [{id: "...", name: "..."}, ...], source: [...] }
      if (frameworksData?.value && Array.isArray(frameworksData.value)) {
        frameworkIds = frameworksData.value
          .filter(f => {
            const isValid = f && typeof f === 'object' && f.id;
            return isValid;
          })
          .map(f => {
            const id = f.id;
            return id;
          });
      } 
      // Handle old nested array structure: [[{id: "...", name: "..."}, ...]]
      else if (Array.isArray(frameworksData) && frameworksData.length > 0) {
        const firstElement = frameworksData[0];
        
        if (Array.isArray(firstElement)) {
          // It's a nested array: [[{id: "...", name: "..."}, ...]]
          frameworkIds = firstElement
            .filter(f => {
              const isValid = f && typeof f === 'object' && f.id;
              return isValid;
            })
            .map(f => {
              const id = f.id;
              return id;
            });
        } else if (typeof firstElement === 'object' && firstElement.id) {
          // It's a regular array: [{id: "...", name: "..."}, ...]
          frameworkIds = frameworksData
            .filter(f => f && typeof f === 'object' && f.id)
            .map(f => f.id);
        }
      }
      
      if (frameworkIds.length > 0) {
        const result = await mapControls({ applicable_frameworks: frameworkIds });
        
        // Check if the AI API response is successful and has data
        if (result.success && result.data) {
          setControlsData(result);
          setMappingComplete(true);
          
          // Save the AI response to backend
          const saveResult = await saveControlMapperToBackend(result.data);
          if (saveResult.success) {
            toast.success("Control mapper data loaded and saved successfully");
          } else {
            toast.warning("Control mapper data loaded but failed to save: " + (saveResult.message || "Unknown error"));
          }
        } else {
          setControlsData(result);
          setMappingComplete(true);
        }
      } else {
        setMappingComplete(true);
      }
    } catch (error) {
      setMappingComplete(true);
    }
  };

  if (loading) {
    return (
      <div className="loading-container h-screen">
        <div className="loading-spinner "></div>
        <p>Loading your compliance dashboard...</p>
        <div className="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">⚠️</div>
        <div className="error-content">
          <div className="error-title">Oops! Something went wrong</div>
          <div className="error-message">{error}</div>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!userInfo || !userInfo.data || userInfo.data.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📊</div>
        <h3>No Data Available</h3>
        <p>We couldn't find any user information to display.</p>
        <button 
          className="refresh-button"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  const userData = userInfo.data[0];
  const frameworks = userData.applicable_compliance_frameworks?.value || [];

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
             case 'overview':
         return (
           <>
             {/* Organization Information Section */}
             <div className="info-card">
               <h2 className="section-title">
                 <span className="section-icon">🏢</span>
                 Organization Information
               </h2>

               
               <div className="grid-layout">
                 <div className="info-column">
                   <InfoItemWithSource 
                     label="Organization Name" 
                     value={userData.organization_name?.value} 
                     sources={userData.organization_name?.source}
                   />
                   <InfoItemWithSource 
                     label="Industry" 
                     value={userData.industry_sectors?.value} 
                     sources={userData.industry_sectors?.source}
                   />
                   <InfoItemWithSource 
                     label="Entity Type" 
                     value={userData.entity_type?.value} 
                     sources={userData.entity_type?.source}
                   />
                   <InfoItemWithSource 
                     label="Headquarters" 
                     value={userData.headquarters_country?.value} 
                     sources={userData.headquarters_country?.source}
                   />
                 </div>
                 <div className="info-column">
                   <InfoItemWithSource 
                     label="Institution ID" 
                     value={userData.institution_id?.value} 
                     sources={userData.institution_id?.source}
                   />
                   <InfoItemWithSource 
                     label="Employees" 
                     value={userData.company_size?.value?.employees} 
                     sources={userData.company_size?.source}
                   />
                   <InfoItemWithSource 
                     label="Revenue" 
                     value={userData.company_size?.value?.revenue_band} 
                     sources={userData.company_size?.source}
                   />
                 </div>
               </div>
             </div>

             {/* Key Services Section */}
             {Array.isArray(userData.key_services?.value) && userData.key_services.value.length > 0 && (
               <div className="info-card">
                 <h3 className="section-title">
                   <span className="section-icon">🔑</span>
                   Key Services
                   <span className="services-count">({userData.key_services.value.length})</span>
                 </h3>

                 <ul className="services-list">
                   {userData.key_services.value.map((service, index) => (
                     <li 
                       key={index} 
                       className="service-item"
                       style={{ animationDelay: `${index * 0.1}s` }}
                     >
                       <span className="service-bullet"></span>
                       {safeValue(service)}
                     </li>
                   ))}
                 </ul>
               </div>
             )}

             {/* Compliance Certifications Section */}
             {Array.isArray(userData.compliance_certifications?.value) && userData.compliance_certifications.value.length > 0 && (
               <div className="info-card">
                 <h3 className="section-title">
                   <span className="section-icon">🏆</span>
                   Compliance Certifications
                   <span className="certifications-count">({userData.compliance_certifications.value.length})</span>
                 </h3>

                 <ul className="certifications-list">
                   {userData.compliance_certifications.value.map((cert, index) => (
                     <li 
                       key={index} 
                       className="certification-item"
                       style={{ animationDelay: `${index * 0.1}s` }}
                     >
                       <span className="certification-bullet">✓</span>
                       {safeValue(cert)}
                     </li>
                   ))}
                 </ul>
               </div>
             )}

             {/* Regulatory Information Section */}
             {Array.isArray(userData.regulatory_registrations?.value) && userData.regulatory_registrations.value.length > 0 && (
               <div className="info-card">
                 <h3 className="section-title">
                   <span className="section-icon">📋</span>
                   Regulatory Information
                   <span className="regulatory-count">({userData.regulatory_registrations.value.length})</span>
                 </h3>

                 <div className="regulatory-grid">
                   {userData.regulatory_registrations.value.map((reg, index) => (
                     <div key={index} className="regulatory-item">
                       <span className="regulatory-label">Registration:</span>
                       <span className="regulatory-value">{safeValue(reg)}</span>
                     </div>
                   ))}
                 </div>
               </div>
             )}

             {/* Regulatory Details Section */}
             {userData.regulatory_details && (
               <div className="info-card">
                 <h3 className="section-title">
                   <span className="section-icon">⚖️</span>
                   Regulatory Details
                 </h3>
                 <div className="regulatory-details-grid">
                   <InfoItemWithSource 
                     label="Supervisory Authority" 
                     value={userData.regulatory_details.supervisory_authority?.value} 
                     sources={userData.regulatory_details.supervisory_authority?.source}
                   />
                   <InfoItemWithSource 
                     label="License Type" 
                     value={userData.regulatory_details.license_type?.value} 
                     sources={userData.regulatory_details.license_type?.source}
                   />
                   <InfoItemWithSource 
                     label="License Number" 
                     value={userData.regulatory_details.license_number?.value} 
                     sources={userData.regulatory_details.license_number?.source}
                   />
                   <InfoItemWithSource 
                     label="LEI Code" 
                     value={userData.regulatory_details.lei_code?.value} 
                     sources={userData.regulatory_details.lei_code?.source}
                   />
                   <InfoItemWithSource 
                     label="Regulatory Status" 
                     value={userData.regulatory_details.regulatory_status?.value} 
                     sources={userData.regulatory_details.regulatory_status?.source}
                   />
                 </div>
               </div>
             )}

             {/* Business Constraints */}
             {userData.business_constraints?.value && Object.keys(userData.business_constraints.value).length > 0 && (
               <div className="info-card">
                 <h3 className="section-title">
                   <span className="section-icon">💰</span>
                   Business Constraints
                 </h3>

                 <div className="grid-layout">
                   <div className="info-column">
                     <InfoItem 
                       label="Budget (USD)" 
                       value={userData.business_constraints.value.budget_usd} 
                     />
                     <InfoItem 
                       label="Preferred Start Date" 
                       value={userData.business_constraints.value.preferred_start_date} 
                     />
                   </div>
                   <div className="info-column">
                     <InfoItem 
                       label="Available Resources" 
                       value={userData.business_constraints.value.available_resources} 
                     />
                   </div>
                 </div>
               </div>
             )}

             {/* Recent Press Section */}
             {Array.isArray(userData.recent_press?.value) && userData.recent_press.value.length > 0 && (
               <div className="info-card">
                 <h3 className="section-title">
                   <span className="section-icon">📰</span>
                   Recent Press
                   <span className="press-count">({userData.recent_press.value.length})</span>
                 </h3>

                 <div className="press-list">
                   {userData.recent_press.value.map((press, index) => (
                     <div key={index} className="press-item">
                       <h4 className="press-title">{safeValue(press.title)}</h4>
                       <div className="press-meta">
                         <span className="press-date">{safeValue(press.date)}</span>
                         <a href={press.url} target="_blank" rel="noopener noreferrer" className="press-link">
                           Read More →
                         </a>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             )}
           </>
         );

      case 'frameworks':
        return (
          <div className="info-card">
            <h3 className="section-title">
              <span className="section-icon">📜</span>
              Applicable Compliance Frameworks
              <span className="framework-count">({frameworks.length})</span>
            </h3>
            {frameworks.length > 0 ? (
              <div className="framework-grid">
                {frameworks.map((framework, index) => (
                  <div 
                    key={framework.id} 
                    className="framework-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                                         <div className="framework-header">
                       <div className="framework-icon">
                         {safeValue(framework.name).charAt(0).toUpperCase()}
                       </div>
                       <h4>{safeValue(framework.name)}</h4>
                     </div>
                     <p className="framework-reason">{safeValue(framework.reason)}</p>
                     <div className="framework-footer">
                       <span className="framework-id">ID: {safeValue(framework.id)}</span>
                     </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <p>No compliance frameworks specified</p>
              </div>
            )}
          </div>
        );

      case 'controls':
        return (
          <div className="info-card">
            <div className="controls-header">
              <h3 className="section-title">
                <span className="section-icon">🛡️</span>
                Mapped Controls
              </h3>
              <div className="controls-stats">
                <span className={`status-badge ${mappingComplete ? 'complete' : 'pending'}`}>
                  {mappingComplete ? '✓ Complete' : '↻ Pending'}
                </span>
                                 {controlsData?.data?.grouped && controlsData?.data?.individual && (
                   <span className="controls-count">
                     {controlsData.data.grouped.length + controlsData.data.individual.length} Controls
                   </span>
                 )}
                           </div>
           </div>
           

           
           {controlsData?.data?.grouped?.length ? (
              <div>
                {controlsData.data.grouped.map((group, groupIndex) => (
                  <div key={groupIndex} className="control-group">
                                         <div className="group-header">
                       <h4>{safeValue(group.name)}</h4>
                       {group.suggested_owner_role_department && (
                         <span className="owner-badge">
                           {safeValue(group.suggested_owner_role_department.name)}
                         </span>
                       )}
                     </div>
                     
                     {group.description && (
                       <div className="group-description">
                    
                         {group.objective && <p><strong>Objective:</strong> {safeValue(group.objective)}</p>}
                       </div>
                     )}
                    
                    <div className="table-container">
                      <table className="controls-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Control Name</th>
                            <th>Description</th>
                            <th>Objective</th>

                            <th>Owner</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.mapped_controls?.map((control, index) => (
                            control ? (
                              <tr 
                                key={`group-${groupIndex}-control-${index}`} 
                                className="control-row"
                                style={{ animationDelay: `${index * 0.05}s` }}
                              >
                                                                 <td className="control-code">{safeValue(control.code)}</td>
                                 <td className="control-name">
                                   <div className="control-name-container">
                                     <div className="control-icon">
                                       {safeValue(control.name).charAt(0).toUpperCase()}
                                     </div>
                                     {safeValue(control.name)}
                                   </div>
                                 </td>
                                 <td className="control-description">
                                   {safeValue(control.description)}
                                 </td>
                                 <td className="control-objective">
                                   {safeValue(control.objective)}
                                 </td>
                                 <td className="control-owner">
                                   {safeValue(group.suggested_owner_role_department?.name)}
                                 </td>
                                 <td className="control-type">
                                   <span className={`type-badge ${safeValue(control.mandatoryType) === 'mandatory' ? 'mandatory' : 'optional'}`}>
                                     {safeValue(control.mandatoryType)}
                                   </span>
                                 </td>
                              </tr>
                            ) : null
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
                
                {/* Handle individual controls separately */}
                {controlsData.data.individual?.length > 0 && (
                  <div className="control-group">
                    <div className="group-header">
                    <h3 className="section-title">
                <span className="section-icon">🛡️</span>
                Additional Controls
              </h3>
                    
                                             <span className="controls-count">{controlsData?.data?.individual?.length || 0} items</span>
                    </div>
                    <div className="table-container">
                      <table className="controls-table">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Control Name</th>
                            <th>Description</th>
                            <th>Owner</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {controlsData.data.individual.map((item, index) => (
                            item?.control ? (
                              <tr 
                                key={`individual-control-${index}`} 
                                className="control-row"
                                style={{ animationDelay: `${index * 0.05}s` }}
                              >
                                                                 <td className="control-code">{safeValue(item.control.code)}</td>
                                 <td className="control-name">
                                   <div className="control-name-container">
                                     <div className="control-icon">
                                       {safeValue(item.control.name).charAt(0).toUpperCase()}
                                     </div>
                                     {safeValue(item.control.name)}
                                   </div>
                                 </td>
                                 <td className="control-description">
                                   {safeValue(item.control.description)}
                                 </td>
                                 <td className="control-owner">
                                   {safeValue(item.suggested_owner_role_department?.name)}
                                 </td>
                                 <td className="control-type">
                                   <span className={`type-badge ${safeValue(item.control.mandatoryType) === 'mandatory' ? 'mandatory' : 'optional'}`}>
                                     {safeValue(item.control.mandatoryType)}
                                   </span>
                                 </td>
                              </tr>
                            ) : null
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state">
                {mappingComplete 
                  ? 'No controls mapped for these frameworks' 
                  : 'Mapping controls from compliance frameworks...'}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className='root'>
      {/* Header with Navigation Tabs */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">
            <span className="title-icon">📋</span>
            Compliance Dashboard
          </h1>
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'frameworks' ? 'active' : ''}`}
              onClick={() => setActiveTab('frameworks')}
            >
              Frameworks ({frameworks.length})
            </button>
            <button 
              className={`tab-button ${activeTab === 'controls' ? 'active' : ''}`}
              onClick={() => setActiveTab('controls')}
            >
                             Controls {controlsData?.data?.grouped && controlsData?.data?.individual ? `(${controlsData.data.grouped.length + controlsData.data.individual.length})` : ''}
            </button>
          </div>
        </div>
      </div>

      {/* Render content based on active tab */}
      {renderTabContent()}

      {/* Footer */}
      <div className="dashboard-footer">
        <p>Last updated: {new Date().toLocaleDateString()}</p>
        <p>Compliance Dashboard v1.0</p>
        <div className="footer-actions">
          <button 
            className="assessment-link"
            onClick={() => window.location.href = '/assessment-score'}
          >
            View Assessment Score
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to safely extract display values
const safeValue = (value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'object') {
    // Handle objects with value/source structure
    if (value.value !== undefined) return String(value.value);
    if (value.name !== undefined) return String(value.name);
    if (value.label !== undefined) return String(value.label);
    // If it's an object, try to stringify it
    try {
      return JSON.stringify(value);
    } catch {
      return 'Complex data';
    }
  }
  return String(value);
};

// Helper component for info items
const InfoItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="info-item">
     <strong>{label}:</strong> <span>{safeValue(value)}</span>
   </div>
 );
const getHostname = (source: string) => {
  try {
    return new URL(source).hostname;
  } catch {
    return source; // أو "Unknown source"
  }
};

// Helper component for info items with source information
// Helper component for info items with source information
const InfoItemWithSource: React.FC<{
  label: string;
  value: any;
  sources?: string[];
}> = ({ label, value, sources = [] }) => (
  <div className="info-item-with-source">
    <div className="info-item">
      <strong>{label}:</strong> <span>{safeValue(value)}</span>

      {sources.length > 0 && (
        <div className="source-info-inline">
          <span className="source-label"> | Sources: </span>

          {sources.map((source, index) => (
            <a
              key={index}
              href={source}
              target="_blank"
              rel="noopener noreferrer"
              className="source-link"
            >
              {getHostname(source)}
            </a>
          ))}
        </div>
      )}
    </div>
  </div>
);


// API Service Functions
async function getInformationUser(): Promise<UserInformation> {
  try {
    const responseData = await fetchUserInformation();
    return Array.isArray(responseData) ? responseData[0] : responseData;
  } catch (error: any) {
    throw new Error(error.message || "Error fetching user information");
  }
}

async function mapControls(data: { applicable_frameworks: string[] }): Promise<ControlsResponse> {
  try {
    const responseData = await mapControlsService({
      applicable_frameworks: data.applicable_frameworks,
    });
    return responseData;
  } catch (error) {
    throw new Error('Error mapping controls');
  }
}

export default UserInformationDashboard;